import { i18n } from "@/plugins/i18n";
import { geographicalService } from "@/services/geographical.service";
import { locationsService } from "@/services/locations.service";
import { traceabilityService } from "@/services/traceability.service";
import { store } from "@/store";
import {
  FormField,
  FormFieldTypes,
  FormModel,
  TableFieldType,
  TableHeader
} from "helix-vue-components";
import { Timezones } from "./timezone";

export const LocationTableMetadata: TableHeader[] = [
  {
    value: "name",
    label: String(i18n.t("location.location_name")),
    fieldComponent: TableFieldType.string,
    conditionalClassFn: () => "primary-text",
    class: "tdt__headers__fieldLong",
    sortable: true,
    descending: true
  },
  {
    value: "license_number",
    label: String(i18n.t("location.license_number")),
    fieldComponent: TableFieldType.string,
    class: "tdt__headers__fieldLong",
    sortable: true,
    descending: true
  },
  {
    value: "location_type",
    label: i18n.t("type").toString(),
    fieldComponent: TableFieldType.enumText,
    enum: {
      RETAIL_MEDICAL: i18n.t("medical"),
      RETAIL_RECREATIONAL: i18n.t("recreational"),
      RETAIL_MEDICAL_RECREATIONAL: i18n.t("medical_recreational")
    },
    class: "tdt__headers__fieldLong",
    sortable: false
  }
];

export const locationFormMetadata: FormField[] = [
  {
    label: String(i18n.t("name")),
    name: "name",
    id: "input_name_locat",
    component: FormFieldTypes.textField,
    options: {
      validationRules: ["required"],
      placeholder: i18n.t("location.location_name").toString(),
      disableCondition: () => {
        const disable = store.getters["LocationModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    label: String(i18n.t("location.address_1")),
    name: "address1",
    id: "input_address_locat",
    component: FormFieldTypes.textField,
    options: {
      validationRules: ["required"],
      placeholder: i18n.t("location.address_1"),
      disableCondition: () => {
        const disable = store.getters["LocationModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    component: FormFieldTypes.textField,
    label: String(i18n.t("location.address_2")),
    name: "address2",
    id: "input_address_2_locat",
    options: {
      placeholder: i18n.t("location.address_2"),
      disableCondition: () => {
        const disable = store.getters["LocationModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    label: String(i18n.t("phone")),
    name: "phone",
    id: "input_phone_locat",
    component: FormFieldTypes.textField,
    options: {
      row: 4,
      flex: 3,
      placeholder: i18n.t("phone"),
      vuetifyProps: {
        mask: "phone"
      },
      disableCondition: () => {
        const disable = store.getters["LocationModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    label: String(i18n.t("country")),
    name: "country_id",
    id: "input_country_locat",
    component: FormFieldTypes.autocomplete,
    options: {
      //   relatedModel: "profile",
      placeholder: i18n.t("location.type_to_select"),
      row: 3,
      flex: 3,
      selectOptions: async () => await geographicalService.getCountries(),
      validationRules: ["required"],
      dependEmitField: "state_id",
      associationText: "name",
      associationValue: "id",
      disableCondition: () => {
        const disable = store.getters["LocationModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    label: String(i18n.t("state")),
    name: "state",
    id: "cbx_state_locat",
    component: FormFieldTypes.autocomplete,
    options: {
      validationRules: ["required"],
      placeholder: i18n.t("location.type_to_select"),
      dependOnField: "country_id",
      vuetifyProps: {
        returnObject: true
      },
      selectOptions: async (arg: FormModel) =>
        geographicalService.getStates(arg.currentModel.country_id),
      associationText: "name",
      row: 3,
      flex: 3,
      disableCondition: () => {
        const disable = store.getters["LocationModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    label: String(i18n.t("city")),
    name: "city",
    id: "cbx_city_locat",
    component: FormFieldTypes.textField,
    options: {
      validationRules: ["required"],
      placeholder: i18n.t("city"),
      disableCondition: () => {
        const disable = store.getters["LocationModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    label: String(i18n.t("zip_code")),
    name: "zip",
    id: "input_zip_locat",
    component: FormFieldTypes.textField,
    options: {
      validationRules: ["required"],
      placeholder: i18n.t("zip_code"),
      disableCondition: () => {
        const disable = store.getters["LocationModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    label: String(i18n.t("location.location_type")),
    name: "location_type",
    id: "input_type_locat",
    component: FormFieldTypes.enum,
    options: {
      placeholder: i18n.t("location.location_type_select"),
      validationRules: ["required"],
      enumSelectOptions: [
        { value: "RETAIL_MEDICAL", text: i18n.t("Medical") },
        { value: "RETAIL_RECREATIONAL", text: i18n.t("Recreational") }
      ],
      row: 6,
      flex: 3,
      disableCondition: () => {
        const disable = store.getters["LocationModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    label: String(i18n.t("location.license_type")),
    name: "license_type",
    id: "input_license_type",
    component: FormFieldTypes.enum,
    options: {
      placeholder: i18n.t("location.license_type_select"),
      validationRules: ["required"],
      enumSelectOptions: [{ value: "RETAIL", text: i18n.t("retail") }],
      row: 6,
      flex: 3
    }
  },
  {
    label: String(i18n.t("location.license_number")),
    name: "license_number",
    id: "input_license_locat",
    component: FormFieldTypes.textField,
    options: {
      placeholder: i18n.t("license_number"),
      validationRules: ["required"],
      disableCondition: () => {
        const disable = store.getters["LocationModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    label: String(i18n.t("location.dea")),
    name: "dea",
    id: "input_license_locat",
    component: FormFieldTypes.textField,
    options: {
      placeholder: i18n.t("location.dea"),
      disableCondition: () => {
        const disable = store.getters["LocationModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    label: String(i18n.t("currency")),
    name: "currency",
    id: "cbx_currency",
    component: FormFieldTypes.autocomplete,
    options: {
      placeholder: i18n.t("currency"),
      validationRules: ["required"],
      selectOptions: () => {
        return geographicalService.getCurrencies();
      },
      associationText: "code",
      associationValue: "code",
      disableCondition: () => {
        const disable = store.getters["LocationModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    label: String(i18n.t("time_zone")),
    name: "timezone_id",
    id: "cbx_time_locat",
    component: FormFieldTypes.autocomplete,
    options: {
      validationRules: ["required"],
      placeholder: i18n.t("location.type_to_select"),
      selectOptions: () => {
        return Timezones;
      },
      associationText: "zone",
      associationValue: "id",
      disableCondition: () => {
        const disable = store.getters["LocationModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    label: String(i18n.t("default_language")),
    name: "locale",
    id: "lang_locat",
    component: FormFieldTypes.autocomplete,
    options: {
      validationRules: ["required"],
      placeholder: i18n.t("preferred_language"),
      selectOptions: () => {
        return locationsService.getLocales();
      },
      associationText: "name",
      associationValue: "id",
      disableCondition: () => {
        const disable = store.getters["LocationModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    label: String(i18n.t("email")),
    name: "email",
    id: "input_email_locat",
    component: FormFieldTypes.textField,
    options: {
      placeholder: i18n.t("auth.enter_email").toString(),
      disableCondition: () => {
        const disable = store.getters["LocationModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    label: "Traceability System",
    name: "traceability_system_id",
    id: "cbx_traceability",
    component: FormFieldTypes.AutocompleteComponent,
    options: {
      validationRules: ["required"],
      selectOptions: async () => traceabilityService.getTraceabilitySystems(),
      vuetifyProps: { returnObject: true },
      disableCondition: () => {
        const disable = store.getters["LocationModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    label: String(i18n.t("date_format")),
    name: "date_format",
    id: "input_date_format_locat",
    component: FormFieldTypes.enum,
    options: {
      validationRules: ["required"],
      placeholder: i18n.t("date_format"),
      enumSelectOptions: [
        { value: "DD-MM-YYYY", text: "DD-MM-YYYY" },
        { value: "MM-DD-YYYY", text: "MM-DD-YYYY" }
      ],
      row: 6,
      flex: 3,
      disableCondition: () => {
        const disable = store.getters["LocationModule/disabledMode"];
        return disable;
      }
    }
  }
];
